import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CreditCardIcon,
  ShoppingBagIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../config/api';

interface Product {
  id: string;
  name: string;
  type: string;
  price: number;
  duration_hours?: number;
  duration_days?: number;
  description: string;
}

interface Payment {
  payment_id: string;
  user_email: string;
  product: Product;
  payment_method: string;
  amount: number;
  status: string;
  created_at: string;
}

interface Stats {
  total_revenue: number;
  total_transactions: number;
  active_subscriptions: number;
  today_revenue: number;
}

export default function AdminPaymentManagement() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [products, setProducts] = useState<Product[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [stats, setStats] = useState<Stats>({
    total_revenue: 0,
    total_transactions: 0,
    active_subscriptions: 0,
    today_revenue: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');

      // 테스트 상품 목록 조회
      const productsRes = await fetch(`${API_BASE_URL}/test-payments/products`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const productsData = await productsRes.json();
      setProducts(productsData.products || []);

      // 결제 내역 조회
      const paymentsRes = await fetch(`${API_BASE_URL}/test-payments/history`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const paymentsData = await paymentsRes.json();
      setPayments(paymentsData.payments || []);

      // 통계 계산
      calculateStats(paymentsData.payments || []);
    } catch (error) {
      console.error('데이터 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (payments: Payment[]) => {
    const total_revenue = payments.reduce((sum, p) => sum + p.amount, 0);
    const total_transactions = payments.length;
    const active_subscriptions = payments.filter(p => 
      p.product.type === 'MONTHLY' || p.product.type === 'UNLIMITED'
    ).length;
    
    const today = new Date().toDateString();
    const today_revenue = payments
      .filter(p => new Date(p.created_at).toDateString() === today)
      .reduce((sum, p) => sum + p.amount, 0);

    setStats({
      total_revenue,
      total_transactions,
      active_subscriptions,
      today_revenue,
    });
  };

  const createTestPayment = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      
      // 랜덤으로 상품 선택
      if (products.length === 0) {
        alert('상품 목록이 없습니다.');
        return;
      }
      
      const randomProduct = products[Math.floor(Math.random() * products.length)];
      
      const response = await fetch(`${API_BASE_URL}/test-payments/purchase?product_id=${randomProduct.id}&payment_method=CARD`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        alert('테스트 결제가 생성되었습니다.');
        fetchData(); // 데이터 새로고침
      } else {
        alert('테스트 결제 생성에 실패했습니다.');
      }
    } catch (error) {
      console.error('테스트 결제 생성 오류:', error);
      alert('테스트 결제 생성 중 오류가 발생했습니다.');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW',
    }).format(amount);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'SUCCESS':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'FAILED':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'PENDING':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getProductTypeLabel = (type: string) => {
    switch (type) {
      case 'TIME':
        return '시간권';
      case 'DAY':
        return '일일권';
      case 'MONTHLY':
        return '정기권';
      case 'UNLIMITED':
        return '무제한권';
      default:
        return type;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">결제 관리</h1>
            <p className="mt-1 text-sm text-gray-500">
              결제 시스템 현황 및 관리 (테스트 모드)
            </p>
          </div>

          {/* 탭 메뉴 */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              {[
                { id: 'overview', name: '개요' },
                { id: 'products', name: '상품 관리' },
                { id: 'payments', name: '결제 내역' },
                { id: 'stats', name: '통계' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                    ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          {/* 탭 내용 */}
          <div className="px-6 py-6">
            {loading ? (
              <div className="text-center py-12">
                <ArrowPathIcon className="h-8 w-8 animate-spin mx-auto text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">데이터 로딩 중...</p>
              </div>
            ) : (
              <>
                {/* 개요 탭 */}
                {activeTab === 'overview' && (
                  <div>
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                      <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="p-5">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <BanknotesIcon className="h-6 w-6 text-gray-400" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                              <dl>
                                <dt className="text-sm font-medium text-gray-500 truncate">
                                  총 매출
                                </dt>
                                <dd className="mt-1 text-2xl font-semibold text-gray-900">
                                  {formatCurrency(stats.total_revenue)}
                                </dd>
                              </dl>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="p-5">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <ShoppingBagIcon className="h-6 w-6 text-gray-400" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                              <dl>
                                <dt className="text-sm font-medium text-gray-500 truncate">
                                  거래 건수
                                </dt>
                                <dd className="mt-1 text-2xl font-semibold text-gray-900">
                                  {stats.total_transactions}건
                                </dd>
                              </dl>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="p-5">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <CreditCardIcon className="h-6 w-6 text-gray-400" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                              <dl>
                                <dt className="text-sm font-medium text-gray-500 truncate">
                                  활성 구독
                                </dt>
                                <dd className="mt-1 text-2xl font-semibold text-gray-900">
                                  {stats.active_subscriptions}건
                                </dd>
                              </dl>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="p-5">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <ChartBarIcon className="h-6 w-6 text-gray-400" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                              <dl>
                                <dt className="text-sm font-medium text-gray-500 truncate">
                                  오늘 매출
                                </dt>
                                <dd className="mt-1 text-2xl font-semibold text-gray-900">
                                  {formatCurrency(stats.today_revenue)}
                                </dd>
                              </dl>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-8">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">
                        결제 시스템 상태
                      </h3>
                      <div className="mb-4">
                        <button
                          onClick={createTestPayment}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <CreditCardIcon className="h-5 w-5 mr-2" />
                          테스트 결제 생성
                        </button>
                      </div>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <DocumentTextIcon className="h-5 w-5 text-yellow-400" />
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-yellow-800">
                              테스트 모드 활성화
                            </h3>
                            <div className="mt-2 text-sm text-yellow-700">
                              <p>현재 테스트 모드로 실행 중입니다. 실제 결제는 이루어지지 않습니다.</p>
                              <ul className="list-disc list-inside mt-2 space-y-1">
                                <li>토스페이먼츠: 테스트 API 키 사용 중</li>
                                <li>카카오페이: 테스트 계정 설정 필요</li>
                                <li>Paymint: 테스트 MID 설정 필요</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 상품 관리 탭 */}
                {activeTab === 'products' && (
                  <div>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                      {products.map((product) => (
                        <div
                          key={product.id}
                          className="bg-white overflow-hidden shadow rounded-lg"
                        >
                          <div className="px-4 py-5 sm:p-6">
                            <h3 className="text-lg font-medium text-gray-900">
                              {product.name}
                            </h3>
                            <p className="mt-1 text-sm text-gray-500">
                              {product.description}
                            </p>
                            <div className="mt-3">
                              <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                                {getProductTypeLabel(product.type)}
                              </span>
                            </div>
                            <p className="mt-3 text-2xl font-semibold text-gray-900">
                              {formatCurrency(product.price)}
                            </p>
                            {product.duration_hours && (
                              <p className="mt-1 text-sm text-gray-500">
                                {product.duration_hours}시간
                              </p>
                            )}
                            {product.duration_days && (
                              <p className="mt-1 text-sm text-gray-500">
                                {product.duration_days}일
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 결제 내역 탭 */}
                {activeTab === 'payments' && (
                  <div>
                    <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-300">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              결제 ID
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              사용자
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              상품
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              금액
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              결제 수단
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              상태
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              결제일
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {payments.map((payment) => (
                            <tr key={payment.payment_id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {payment.payment_id.slice(-8)}...
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {payment.user_email}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {payment.product.name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatCurrency(payment.amount)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {payment.payment_method}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  {getStatusIcon(payment.status)}
                                  <span className="ml-2 text-sm text-gray-500">
                                    {payment.status}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {new Date(payment.created_at).toLocaleDateString('ko-KR')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {payments.length === 0 && (
                        <div className="text-center py-8">
                          <p className="text-gray-500">결제 내역이 없습니다.</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* 통계 탭 */}
                {activeTab === 'stats' && (
                  <div className="space-y-6">
                    <div className="bg-white shadow rounded-lg p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">
                        결제 수단별 분석
                      </h3>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-500">카드</span>
                          <span className="text-sm font-medium text-gray-900">
                            {payments.filter(p => p.payment_method === 'CARD').length}건
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-500">카카오페이</span>
                          <span className="text-sm font-medium text-gray-900">
                            {payments.filter(p => p.payment_method === 'KAKAO').length}건
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white shadow rounded-lg p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">
                        상품별 매출
                      </h3>
                      <div className="space-y-2">
                        {products.map((product) => {
                          const productPayments = payments.filter(
                            p => p.product.id === product.id
                          );
                          const revenue = productPayments.reduce(
                            (sum, p) => sum + p.amount,
                            0
                          );
                          return (
                            <div key={product.id} className="flex justify-between items-center">
                              <span className="text-sm text-gray-500">{product.name}</span>
                              <span className="text-sm font-medium text-gray-900">
                                {formatCurrency(revenue)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}